import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Loader2, Rocket, Power } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../lib/apiClient';

export const LaunchControlTab = () => {
  const [preLaunchMode, setPreLaunchMode] = useState(null);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [toggling, setToggling] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiGet('/api/admin/pre-launch/status');
      setPreLaunchMode(data.pre_launch_mode);
      setWaitlistCount(data.waitlist_count);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const data = await apiPost('/api/admin/pre-launch/toggle');
      setPreLaunchMode(data.pre_launch_mode);
      toast.success(data.message);
    } catch { toast.error('Failed to toggle'); }
    finally { setToggling(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-4 flex items-center gap-2">
          <Rocket className="w-5 h-5" />
          Launch Control
        </h3>
        <div className="flex items-center justify-between p-4 rounded-xl border-2 border-dashed border-slate-200">
          <div>
            <p className="font-semibold text-slate-900 text-lg">
              {preLaunchMode ? 'Pre-Launch Mode: ACTIVE' : 'Application: LIVE'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {preLaunchMode
                ? 'Visitors see the pre-launch wrapper with waitlist form. Login and signup are blocked (admin bypass via /login?admin=true).'
                : 'Full application is live. All pages, login, and signup are accessible to everyone.'}
            </p>
          </div>
          <Button
            onClick={handleToggle}
            disabled={toggling}
            className={`min-w-[160px] ${preLaunchMode
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-amber-600 hover:bg-amber-700 text-white'
            }`}
            data-testid="toggle-pre-launch-btn"
          >
            {toggling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Power className="w-4 h-4 mr-2" />}
            {preLaunchMode ? 'Go Live' : 'Enable Pre-Launch'}
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-1">Waitlist Signups</p>
            <p className="text-2xl font-bold text-slate-900">{waitlistCount}</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-1">Status</p>
            <p className={`text-2xl font-bold ${preLaunchMode ? 'text-amber-600' : 'text-emerald-600'}`}>
              {preLaunchMode ? 'Pre-Launch' : 'Live'}
            </p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-3">Admin Bypass</h3>
        <p className="text-sm text-slate-600 mb-2">
          When pre-launch mode is active, admins can access the login page using:
        </p>
        <code className="block bg-slate-100 text-slate-800 px-4 py-2 rounded-lg text-sm font-mono">
          {window.location.origin}/login?admin=true
        </code>
      </div>
    </div>
  );
};
